import { formatCurrency, formatPercent, formatRatio } from "@/lib/format";

export interface FairValueStatus {
  text: string;
  direction: "under" | "over" | "equal";
}

/** Unterhalb dieser Schwelle gilt das KGV als "auf Höhe des Median" statt
 * unter/über - vermeidet eine irrefuehrende Richtungsaussage bei Rauschen. */
const EQUAL_THRESHOLD = 0.02;

/**
 * Deskriptiver Fair-Value-Status-Chip ("X % unter/über historischem Median-KGV") fuer
 * die Bewertung-Sektion (siehe Feedback-Runde 4, Punkt 5) - reine Funktion, kein I/O.
 * Bevorzugt den echten Sektor-Median (Benchmark.peSector, seit Feedback-Runde 3
 * vorhanden), faellt sonst auf den eigenen historischen Median zurueck - identische
 * Praeferenz wie die Score-KPI 7 (scorePeVsMedian in lib/scoring/scoring.ts), hier aber
 * rein beschreibend statt punktebewertend. Rein deskriptiv, keine Handlungssprache.
 */
export function describeFairValueStatus(
  pe: number | undefined,
  sectorMedian: number | undefined,
  ownMedian: number | undefined,
): FairValueStatus | null {
  const usedSector = sectorMedian !== undefined && sectorMedian > 0;
  const median = usedSector ? sectorMedian : ownMedian;
  if (pe === undefined || pe <= 0 || median === undefined || median <= 0) return null;

  const discount = (median - pe) / median;
  const medianLabel = usedSector ? "Sektor-Median" : "eigenen historischen Median";

  if (Math.abs(discount) < EQUAL_THRESHOLD) {
    return {
      text: `KGV ${formatRatio(pe)} liegt auf Höhe des ${medianLabel}s (${formatRatio(median)}).`,
      direction: "equal",
    };
  }

  const direction = discount > 0 ? "under" : "over";
  const verb = discount > 0 ? "unter" : "über";
  return {
    text: `KGV ${formatRatio(pe)} liegt ${formatPercent(Math.abs(discount))} ${verb} dem ${medianLabel} (${formatRatio(median)}).`,
    direction,
  };
}

/**
 * Kaufpreis-vs-Fair-Value-Ampel im Depot (backlog.md -> Weiterer Nutzwert-Katalog,
 * "Kaufpreis-vs-Fair-Value-Ampel im Depot ... rein retrospektive Einordnung, ob der
 * eigene Einstieg günstig oder teuer war") - reine Funktion, kein I/O. Vergleicht den
 * eigenen Einstandskurs NICHT gegen den aktuellen Kurs (das zeigt die Depot-Tabelle
 * bereits als Gewinn/Verlust), sondern gegen den unabhängig davon berechneten fairen
 * Wert von heute (ScoreSummary.fairValueNow, dieselbe TTM-EPS x historisches-Durchschnitts-
 * KGV-Methode wie die Bewertung-Sektion) - beantwortet "war mein Einstieg im Rückblick
 * fundamental günstig?", nicht "habe ich aktuell Gewinn gemacht?". Gleiches
 * Schwellenwert-/Richtungs-Muster wie describeFairValueStatus oben, damit beide Ampeln
 * sich fuer Nutzer konsistent anfuehlen.
 */
export function describePurchaseVsFairValue(
  purchasePrice: number | undefined,
  fairValueNow: number | undefined,
  currency: string | undefined,
): FairValueStatus | null {
  if (purchasePrice === undefined || purchasePrice <= 0 || fairValueNow === undefined || fairValueNow <= 0) {
    return null;
  }

  const discount = (fairValueNow - purchasePrice) / fairValueNow;
  const purchaseLabel = formatCurrency(purchasePrice, currency ?? "USD");
  const fairValueLabel = formatCurrency(fairValueNow, currency ?? "USD");

  if (Math.abs(discount) < EQUAL_THRESHOLD) {
    return {
      text: `Einstandskurs ${purchaseLabel} liegt auf Höhe des heutigen fairen Werts (${fairValueLabel}).`,
      direction: "equal",
    };
  }

  const direction = discount > 0 ? "under" : "over";
  const verb = discount > 0 ? "unter" : "über";
  return {
    text: `Einstandskurs ${purchaseLabel} liegt ${formatPercent(Math.abs(discount))} ${verb} dem heutigen fairen Wert (${fairValueLabel}) - rein retrospektive Einordnung, keine Kauf-/Verkaufsempfehlung.`,
    direction,
  };
}
